import { ArrowsClockwiseIcon, CaretLeftIcon, TrashIcon } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm'

import Container from 'components/Container';

import { getDocument, reloadDocument } from 'api/documents';
import Page from 'components/Layout/Page';

export default function Detail() {

    const [ , setRemovePopup ] = useState(false);
    const { folderId, documentId } = useParams();

    const cache = useQueryClient();

    const { data: document, isLoading } = useQuery({
        queryKey: ['documents', documentId],
        queryFn: async () => await getDocument({id: documentId}),
        enabled: Boolean(documentId),
    });

    const { mutate } = useMutation({
        mutationKey: ['reloadDocument'],
        mutationFn: reloadDocument,
        onSuccess: () => {
            cache.invalidateQueries(['documents', folderId, document._id]);
        },
        onError: (error) => {
            console.error("Error updating document-tag:", error);
        }
    });

    const removePopupHandler = (e) => {
        e.preventDefault();
        setRemovePopup(true);
    }

    const reloadHandler = (e) => {
        e.preventDefault();
        return mutate({ id: documentId });
    }

    return (
        <>
            <Page isLoading={isLoading}>
                <Container>
                    <div className='pb-8 pt-20 md:pt-14 flex justify-between items-center'>
                        <div className='flex gap-3 items-center'>
                            <Link to={`/settings/documents/${folderId}`} className=''>
                                <CaretLeftIcon size={26} className='hover:text-muted' />
                            </Link>
                            <div>
                                <p className='text-sm uppercase font-semibold'>{document?.folder?.name}</p>
                                <p className='text-2xl'>{document?.name}</p>
                            </div>
                        </div>
                        
                        <div className='flex items-center justify-end gap-4'>
                            <div className='' onClick={reloadHandler}>
                                <ArrowsClockwiseIcon size={20} className='inline-block mb-1' />
                            </div>
                            <div className='' onClick={removePopupHandler}>
                                <TrashIcon size={20} className='inline-block mb-1' />
                            </div>
                        </div>
                    </div>

                    <div className='flex gap-10'>
                        <div className='flex-1 border border-light rounded-sm'>
                            <div className='p-4'>
                                <label className='text-sm mb-1 block'>Descrizione</label>
                                <p className='text-muted'>{document?.description || "Nessuna descrizione"}</p>
                            </div>
                            <div className='p-4'>
                                <label className='text-sm mb-1 block'>Sommario</label>
                                <p className='text-muted'>{document?.summary || "Nessun sommario"}</p>
                            </div>
                            <div className='p-4'>
                                <label className='text-sm mb-1 block'>Lingua</label>
                                <p className='text-muted'>{document?.language || "Nessuna lingua rilevata"}</p>
                            </div>
                            <div className='p-4'>
                                <label className='text-sm mb-1 block'>Parole chiave</label>
                                <p className='text-muted'>{document?.keywords?.join(', ') || "Nessuna parola chiave"}</p>
                            </div>
                            <div className='p-4'>
                                <label className='text-sm mb-1 block'>Tipo di documento</label>
                                <p className='text-muted'>{document?.document_type || "Nessun tipo di documento"}</p>
                            </div>
                        </div>
                    </div>

                    <div className='border border-light rounded-sm mt-6'>
                        {(document?.chunks?.length === 0) && (
                            <div className='p-4'>
                                <p className='text-muted'>Nessuna porzione del file registrata.</p>
                            </div>
                        )}
                        {document?.chunks?.map(chunk => {
                            return (
                                <div key={chunk._id} className=' group border-b last:border-0 border-light flex items-center gap-4 p-4'>
                                    <div className='flex-1'>
                                        <p className='text-sm font-semibold mb-2'>Chunk #{chunk.index + 1} {chunk.sensibility?.sensitive ? <span className='text-red-500'>(Sensibile)</span> : <span className='text-green-500'>(Non sensibile)</span>}</p>
                                        <p className='text-xs'>
                                            <Markdown remarkPlugins={[remarkGfm]}>{chunk.content}</Markdown>
                                        </p>
                                        {chunk.sensibility?.classification && (
                                            <div className='mt-2'>
                                                <p className='text-xs font-semibold mb-1'>Classificazione della sensibilità:</p>
                                                <div className='flex flex-wrap gap-2'>
                                                    {chunk.sensibility.classification.map((item, index) => {
                                                        const sensibile = item.score > 0.7;
                                                        return (
                                                            <span key={index} className={`text-xs bg-verylight px-2 py-1 rounded-sm ${sensibile ? 'text-red-500' : 'text-grey-200'}`}>{item.label} ({(item.score * 100).toFixed(2)}%){sensibile ? `: ${item.text}` : ''}</span>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </Container>
            </Page>
        </>
    )
}
